<template>
    <v-card flat class="auth-flow-card">
        <div class="auth-flow-card__inner">
            <header class="auth-flow-card__brand">
                <img :src="araLogo2" alt="" class="auth-flow-card__logo" width="36" height="53" />
                <div class="auth-flow-card__brand-text">
                    <div class="app-typography-brand-title">Provider Network Alliance</div>
                    <div class="auth-flow-card__brand-subtitle">
                        Contracts, Credentials &amp; Corporate Governance Management Portal
                    </div>
                </div>
            </header>

            <div class="auth-flow-card__body">
                <slot />
            </div>

            <p v-if="showTerms" class="auth-flow-card__terms">
                By logging in ARA's proprietary software, you agree to our
                <NuxtLink
                    :to="termsLink"
                    class="auth-flow-card__terms-link"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Terms &amp; Conditions
                </NuxtLink>
            </p>
        </div>
    </v-card>
</template>

<script setup lang="ts">
import araLogo2 from '~/assets/img/ara-logo.svg'
import { AUTH_TERMS_ROUTE } from '~/config/auth'

const props = withDefaults(
    defineProps<{
        showTerms?: boolean
        /** Optional query params for the terms route (e.g. redirect back to login). */
        termsQuery?: Record<string, string>
    }>(),
    {
        showTerms: false,
        termsQuery: () => ({}),
    },
)

const termsLink = computed(() => ({
    path: AUTH_TERMS_ROUTE,
    query: props.termsQuery,
}))
</script>

<style scoped>
/*
 * Figma frame: 709×889, radius 22px, ~68px viewport inset.
 * Width is fluid up to the design max; height grows with content and
 * only applies a design min-height when the viewport has room.
 */
.auth-flow-card {
    --auth-card-max-width: 709px;
    --auth-card-min-height: 889px;
    --auth-card-radius: 22px;
    --auth-card-padding-inline: clamp(24px, 12vw, 90px);
    --auth-card-padding-block: clamp(28px, 4vh, 40px);
    --auth-card-brand-to-step-gap: 60px;

    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: var(--auth-card-max-width);
    margin-inline: auto;
    min-height: auto;
    padding: var(--auth-card-padding-block) var(--auth-card-padding-inline);
    border-radius: var(--auth-card-radius);
    background: rgb(var(--v-theme-surface));
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.auth-flow-card__inner {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    flex: 0 1 auto;
    margin: auto;
}

@media (min-width: 600px) and (min-height: 720px) {
    .auth-flow-card {
        min-height: min(
            var(--auth-card-min-height),
            calc(100dvh - 2 * var(--auth-page-inset-block, 68px))
        );
    }
}

.auth-flow-card__brand {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    width: 100%;
    margin-bottom: 0;
    flex-shrink: 0;
}

.auth-flow-card__logo {
    flex-shrink: 0;
    width: 36px;
    height: auto;
    object-fit: contain;
}

.auth-flow-card__brand-text {
    min-width: 0;
    padding-top: 2px;
}

.auth-flow-card__brand-subtitle {
    margin-top: 6px;
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.4;
    color: rgb(var(--v-theme-neutral));
}

.auth-flow-card__body {
    width: 100%;
    min-width: 0;
    margin-top: var(--auth-card-brand-to-step-gap);
}

.auth-flow-card__body :deep(.auth-step) {
    width: 100%;
}

.auth-flow-card__body :deep(.app-typography-title-1) {
    text-align: left;
}

.auth-flow-card__terms {
    margin: 24px 0 0;
    flex-shrink: 0;
    font-family: Inter, sans-serif;
    font-size: 12px;
    line-height: 1.5;
    text-align: center;
    color: rgb(var(--v-theme-neutral));
}

.auth-flow-card__terms-link {
    color: rgb(var(--v-theme-primary));
    font-weight: 500;
    text-decoration: none;
}

.auth-flow-card__terms-link:hover {
    text-decoration: underline;
}
</style>
